"use client";

import Link from "next/link";
import { ArrowRight, LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface Usp {
  title: string;
  body: string;
  icon: LucideIcon;
  accent: string;
}

export interface UspsSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  intro?: string;
  usps: Usp[];
  cta: { label: string; href: string };
  accent: string;
  gridCols?: 2 | 3;
}

export function UspsSection({
  id = "usps",
  eyebrow,
  title,
  intro,
  usps,
  cta,
  accent,
  gridCols = 3,
}: UspsSectionProps) {
  return (
    <Section id={id} className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: accent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
            {intro && (
              <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{intro}</p>
            )}
          </div>
        </Reveal>

        <div className={`mt-12 grid gap-5 ${gridCols === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
          {usps.map((u, i) => {
            const Icon = u.icon;
            return (
              <Reveal key={u.title} delay={i * 80}>
                <div
                  className="glass rounded-2xl p-6 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${u.accent} 12%, transparent)`,
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="grid place-items-center h-10 w-10 rounded-lg"
                      style={{
                        background: `color-mix(in oklch, ${u.accent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${u.accent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-4 w-4" style={{ color: u.accent }} />
                    </span>
                    <span className="eyebrow-tiny font-bold" style={{ color: u.accent }}>
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="text-lg">{u.title}</h3>
                  <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{u.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={500}>
          <div className="mt-10">
            <Link
              href={cta.href}
              className="btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
            >
              {cta.label} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
